import { createBrowserRouter, Navigate, Outlet, ScrollRestoration } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import RootLayout from './routes/__root'

const fallback = (
  <div className="h-56 border border-border/70 bg-card/70 animate-pulse" />
)

function lazyRoute(importFn: () => Promise<{ default: React.ComponentType }>) {
  const LazyComponent = lazy(importFn)
  return function LazyRouteWrapper() {
    return (
      <Suspense fallback={fallback}>
        <LazyComponent />
      </Suspense>
    )
  }
}

const IndexPage = lazyRoute(() => import('./routes/index'))
const RepositoriesPage = lazyRoute(() => import('./routes/repositories'))
const ExplorePage = lazyRoute(() => import('./routes/explore'))
const LoginPage = lazyRoute(() => import('./routes/login'))
const RegisterPage = lazyRoute(() => import('./routes/register'))
const NewPage = lazyRoute(() => import('./routes/new'))
const ImportPage = lazyRoute(() => import('./routes/import'))
const OrgNewPage = lazyRoute(() => import('./routes/org/new'))
const OrgsPage = lazyRoute(() => import('./routes/orgs'))
const OrgSlugPage = lazyRoute(() => import('./routes/org/$slug'))
const OrgSettingsPage = lazyRoute(() => import('./routes/org/$slug.settings'))
const OwnerProfilePage = lazyRoute(() => import('./routes/$owner'))
const OwnerRepoLayout = lazyRoute(() => import('./routes/$owner.$repo'))
const OwnerRepoIndexPage = lazyRoute(() => import('./routes/$owner.$repo.index'))
const OwnerRepoContributorsPage = lazyRoute(() => import('./routes/$owner.$repo.contributors'))
const OwnerRepoCommitsPage = lazyRoute(() => import('./routes/$owner.$repo.commits'))
const OwnerRepoCommitShaPage = lazyRoute(() => import('./routes/$owner.$repo.commit.$sha'))
const OwnerRepoIssuesPage = lazyRoute(() => import('./routes/$owner.$repo.issues'))
const OwnerRepoIssueNewPage = lazyRoute(() => import('./routes/$owner.$repo.issues.new'))
const OwnerRepoIssueNumberPage = lazyRoute(() => import('./routes/$owner.$repo.issues.$number'))
const OwnerRepoPullsPage = lazyRoute(() => import('./routes/$owner.$repo.pulls'))
const OwnerRepoPullNewPage = lazyRoute(() => import('./routes/$owner.$repo.pull.new'))
const OwnerRepoPullNumberPage = lazyRoute(() => import('./routes/$owner.$repo.pull.$number'))
const OwnerRepoReleasesPage = lazyRoute(() => import('./routes/$owner.$repo.releases'))
const OwnerRepoReleaseNewPage = lazyRoute(() => import('./routes/$owner.$repo.releases.new'))
const OwnerRepoReleaseTagPage = lazyRoute(() => import('./routes/$owner.$repo.releases.tag.$tag'))
const OwnerRepoReleaseEditPage = lazyRoute(
  () => import('./routes/$owner.$repo.releases.tag.$tag.edit'),
)
const OwnerRepoSettingsPage = lazyRoute(() => import('./routes/$owner.$repo.settings'))
const OwnerRepoTreeBranchSplatPage = lazyRoute(() => import('./routes/$owner.$repo.tree.$branch.$'))
const OwnerRepoBlobBranchSplatPage = lazyRoute(() => import('./routes/$owner.$repo.blob.$branch.$'))
const SettingsPage = lazyRoute(() => import('./routes/settings'))
const SettingsKeysPage = lazyRoute(() => import('./routes/settings.keys'))
const IssuesDashboardPage = lazyRoute(() => import('./routes/issues'))
const PullsDashboardPage = lazyRoute(() => import('./routes/pulls'))
const ForgotPasswordPage = lazyRoute(() => import('./routes/forgot-password'))
const ResetPasswordPage = lazyRoute(() => import('./routes/reset-password'))
const TwoFactorPage = lazyRoute(() => import('./routes/two-factor'))
const SearchSyntaxPage = lazyRoute(() => import('./routes/docs/search-syntax'))
const WaitlistVerifyPage = lazyRoute(() => import('./routes/waitlist-verify'))
const WaitlistRemovePage = lazyRoute(() => import('./routes/waitlist-remove'))
const ReportPage = lazyRoute(() => import('./routes/report'))

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, element: <IndexPage /> },
      { path: 'repositories', element: <RepositoriesPage /> },
      { path: 'explore', element: <ExplorePage /> },
      { path: 'login', element: <LoginPage /> },
      { path: 'register', element: <RegisterPage /> },
      { path: 'forgot-password', element: <ForgotPasswordPage /> },
      { path: 'reset-password', element: <ResetPasswordPage /> },
      { path: 'new', element: <NewPage /> },
      { path: 'import', element: <ImportPage /> },
      { path: 'orgs', element: <OrgsPage /> },
      { path: 'org/new', element: <OrgNewPage /> },
      { path: 'org/:slug', element: <OrgSlugPage /> },
      { path: 'org/:slug/settings', element: <OrgSettingsPage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'settings/keys', element: <SettingsKeysPage /> },
      { path: 'issues', element: <IssuesDashboardPage /> },
      { path: 'pulls', element: <PullsDashboardPage /> },
      { path: 'two-factor', element: <TwoFactorPage /> },
      { path: 'waitlist/verify', element: <WaitlistVerifyPage /> },
      { path: 'waitlist/remove', element: <WaitlistRemovePage /> },
      { path: 'report', element: <ReportPage /> },
      { path: 'docs/search-syntax', element: <SearchSyntaxPage /> },
      { path: ':owner', element: <OwnerProfilePage /> },
      {
        path: ':owner/:repo',
        element: <OwnerRepoLayout />,
        children: [
          { index: true, element: <OwnerRepoIndexPage /> },
          { path: 'contributors', element: <OwnerRepoContributorsPage /> },
          { path: 'commits', element: <OwnerRepoCommitsPage /> },
          { path: 'commit/:sha', element: <OwnerRepoCommitShaPage /> },
          { path: 'issues', element: <OwnerRepoIssuesPage /> },
          { path: 'issues/new', element: <OwnerRepoIssueNewPage /> },
          { path: 'issues/:number', element: <OwnerRepoIssueNumberPage /> },
          { path: 'pulls', element: <OwnerRepoPullsPage /> },
          { path: 'pull/new', element: <OwnerRepoPullNewPage /> },
          { path: 'pull/:number', element: <OwnerRepoPullNumberPage /> },
          { path: 'releases', element: <OwnerRepoReleasesPage /> },
          { path: 'releases/new', element: <OwnerRepoReleaseNewPage /> },
          { path: 'releases/tag/:tag', element: <OwnerRepoReleaseTagPage /> },
          { path: 'releases/tag/:tag/edit', element: <OwnerRepoReleaseEditPage /> },
          { path: 'settings', element: <OwnerRepoSettingsPage /> },
          { path: 'tree/:branch/*', element: <OwnerRepoTreeBranchSplatPage /> },
          { path: 'blob/:branch/*', element: <OwnerRepoBlobBranchSplatPage /> },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
])

export function Router() {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  )
}
